import { Position, Node as FlowNode, Edge as FlowEdge } from "reactflow";
import dagre from '@dagrejs/dagre';

const nodeWidth = 180;
const nodeHeight = 120;
const NODE_MARGIN = 250;
const dagreGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));

const getLayoutedElements = (nodes: FlowNode[], edges: FlowEdge[], direction = 'LR') => {
    // Reset the dagre graph before setting up new nodes and edges
    // const dagreGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));

    const isHorizontal = direction === 'LR';
    dagreGraph.setGraph({ 
        rankdir: direction,
        align: 'UL',              // Upper-left alignment for consistent positioning
        nodesep: 80,              // Increased horizontal spacing between nodes in same rank
        ranksep: 200,             // Increased vertical spacing between ranks for better clarity
        edgesep: 20,              // Separation between parallel edges to reduce overlap
        acyclicer: 'greedy',      // Use greedy cycle removal for better layout
        ranker: 'network-simplex', // Network-simplex ranker for better balanced layouts
    });

    // Build adjacency maps for topological sorting
    const targetNodeIds = new Set(edges.map(edge => edge.target));
    const childrenMap = new Map<string, string[]>(); // nodeId -> array of child nodeIds
    const parentsMap = new Map<string, string[]>(); // nodeId -> array of parent nodeIds
    
    edges.forEach(edge => {
        // Build children map (outgoing edges)
        if (!childrenMap.has(edge.source)) {
            childrenMap.set(edge.source, []);
        }
        childrenMap.get(edge.source)!.push(edge.target);
        
        // Build parents map (incoming edges)
        if (!parentsMap.has(edge.target)) {
            parentsMap.set(edge.target, []);
        }
        parentsMap.get(edge.target)!.push(edge.source);
    });
    
    // Find start node (node with no incoming edges)
    const startNodeId = nodes.find(node => !targetNodeIds.has(node.id))?.id;
    
    // Topologically sort nodes starting from start node (BFS order)
    const sortedNodeIds: string[] = [];
    const visited = new Set<string>();
    
    if (startNodeId) {
        // BFS starting from start node
        const queue: string[] = [startNodeId];
        visited.add(startNodeId);
        
        while (queue.length > 0) {
            const currentNodeId = queue.shift()!;
            sortedNodeIds.push(currentNodeId);
            
            // Add all children to queue
            const children = childrenMap.get(currentNodeId) || [];
            children.forEach(childId => {
                if (!visited.has(childId)) {
                    // Check if all parents are visited (for nodes with multiple parents)
                    const parents = parentsMap.get(childId) || [];
                    if (parents.every(parentId => visited.has(parentId))) {
                        visited.add(childId);
                        queue.push(childId);
                    }
                }
            });
        }
    }
    
    // Add any remaining nodes (disconnected nodes) in their original order
    nodes.forEach(node => {
        if (!visited.has(node.id)) {
            sortedNodeIds.push(node.id);
        }
    });
    
    // Create sorted nodes array based on topological order
    const nodeMap = new Map(nodes.map(node => [node.id, node]));
    const sortedNodes = sortedNodeIds.map(id => nodeMap.get(id)).filter((node): node is typeof nodes[0] => node !== undefined);

    sortedNodes.forEach((node) => {
        dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
    });

    edges.forEach((edge) => {
        dagreGraph.setEdge(edge.source, edge.target);
    });

    dagre.layout(dagreGraph);

    // Calculate initial positions (use original nodes array to preserve order)
    let layoutedNodes = nodes.map((node) => {
        const nodeWithPosition = dagreGraph.node(node.id);
        return {
            ...node,
            targetPosition: isHorizontal ? Position.Left : Position.Top,
            sourcePosition: isHorizontal ? Position.Right : Position.Bottom,
            position: {
                x: nodeWithPosition.x - nodeWidth / 2,
                y: nodeWithPosition.y - nodeHeight / 2,
            },
        };
    });

    // Reuse startNodeId from above for centering
    
    if (startNodeId && layoutedNodes.length > 1) {
        const startNode = layoutedNodes.find(node => node.id === startNodeId);
        if (startNode) {
            // Calculate the bounding box of ALL nodes (including start node)
            const allNodeCenters = layoutedNodes.map(node => node.position.y + nodeHeight / 2);
            const minCenterY = Math.min(...allNodeCenters);
            const maxCenterY = Math.max(...allNodeCenters);
            
            // Calculate the center of the entire layout
            const layoutCenterY = (minCenterY + maxCenterY) / 2;
            
            // Calculate the start node's current center Y
            const startNodeCenterY = startNode.position.y + nodeHeight / 2;
            
            // Shift all nodes so the start node is at the layout center
            // This ensures nodes are distributed above and below the start node
            const shiftY = layoutCenterY - startNodeCenterY;
            
            // Apply shift to all nodes
            layoutedNodes = layoutedNodes.map(node => ({
                ...node,
                position: {
                    ...node.position,
                    y: node.position.y + shiftY,
                },
            }));
        }
    }

    return {
        nodes: layoutedNodes, 
        edges: edges.map(edge => ({
            ...edge,
            type: 'custom',
            // Preserve original edge styles (including optional connection styling)
            style: edge.style || { 
                stroke: '#B1B1B1', 
                strokeWidth: 2,
            },
        }))
    };
};

export default getLayoutedElements;